import { useContext, useEffect, useMemo } from 'react';
import { useFlowTools } from 'flowTools/useFlowTools';
import { useSearchId, useTrackingInfo } from '../SearchResponseProvider';
import {
  useSemItinerary,
  useSemSearchId,
  useSemTrackingInfo,
} from '../SEM/SemItinerarySelectionProvider/SemItineraryProvider';
import { useShoppingCartRequestCreator } from './ShoppingCartRequestCreatorProvider';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import { useCreateShoppingBundleActions } from 'clientState/providers/CreateShoppingBundleProvider';
import { useItineraryFeeTypeId } from 'clientState/providers/ItinerarySelectionProvider/ItineraryFeeTypeProvider';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import { ItinerarySelectionActionsContext } from './ItinerarySelectionActionsContext';
import useAlternativeFlight from 'clientState/hooks/useAlternativeFlight';

const hasSegmentKeys = (legs) =>
  legs.every(({ segmentKeys }) => segmentKeys && segmentKeys.length > 0);

export const useShoppingCartRequestEffect = ({ isSem = false }) => {
  const { selectItinerary } = useContext(ItinerarySelectionActionsContext);
  const { createShoppingBundle } = useCreateShoppingBundleActions();
  const itineraryFeeTypeId = useItineraryFeeTypeId();
  const resultsItinerary = useItinerary();
  const semItinerary = useSemItinerary();
  const itinerary = isSem ? semItinerary : resultsItinerary;
  const [feeType] = useFeeType();
  const { isMobile } = useFlowTools();
  const resultsSearchId = useSearchId();
  const semSearchId = useSemSearchId();
  const searchId = isSem ? semSearchId : resultsSearchId;
  const resultsTrackingInfo = useTrackingInfo();
  const semTrackingInfo = useSemTrackingInfo();
  const trackingInfo = isSem ? semTrackingInfo : resultsTrackingInfo;
  const createItinerarySelection = useShoppingCartRequestCreator();
  const feeTypeId = feeType?.id;
  const { setAlternativeFlight } = useAlternativeFlight();

  useEffect(() => {
    if (
      searchId &&
      searchId !== 0 &&
      itinerary &&
      itinerary.legs &&
      hasSegmentKeys(itinerary.legs)
    ) {
      const alternativeFlight = {
        originalItineraryId: itinerary?.id,
        selectedItinerary: itinerary,
      };
      setAlternativeFlight(alternativeFlight);
      const feeId = itineraryFeeTypeId || feeTypeId;
      const itinerarySelection = createItinerarySelection(
        itinerary,
        {
          feeTypeId: feeId,
          searchId,
        },
        trackingInfo
      );
      if (isMobile) selectItinerary(itinerarySelection);
      createShoppingBundle({
        ...itinerarySelection,
        feeTypeId: feeId,
      });
    }
  }, [
    isMobile,
    searchId,
    selectItinerary,
    itineraryFeeTypeId,
    itinerary,
    trackingInfo,
    createItinerarySelection,
    feeTypeId,
    createShoppingBundle,
    setAlternativeFlight,
  ]);
};

export const usePreCreateShoppingCartRequestEffect = ({ itinerary = null }) => {
  const { isDesktop } = useFlowTools();
  const { selectItinerary, prepareShoppingCart } = useContext(
    ItinerarySelectionActionsContext
  );
  const itineraryFeeTypeId = useItineraryFeeTypeId();
  const [feeType] = useFeeType();
  const resultsSearchId = useSearchId();
  const searchId = resultsSearchId;
  const resultsTrackingInfo = useTrackingInfo();
  const trackingInfo = resultsTrackingInfo;
  const createItinerarySelection = useShoppingCartRequestCreator();
  const feeTypeId = feeType?.id;

  useEffect(() => {
    if (
      isDesktop &&
      searchId &&
      searchId !== 0 &&
      itinerary &&
      itinerary.legs &&
      hasSegmentKeys(itinerary.legs)
    ) {
      const feeId = itineraryFeeTypeId || feeTypeId;
      const itinerarySelection = createItinerarySelection(
        itinerary,
        {
          feeTypeId: feeId,
          searchId,
        },
        trackingInfo
      );
      selectItinerary(itinerarySelection);
      prepareShoppingCart({
        ...itinerarySelection,
        feeTypeId: feeId,
      });
    }
    // Use the itinerary id instead of the itinerary as a dependency
    // in case the results are rendered twice in a row due to filters
    // dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    searchId,
    selectItinerary,
    prepareShoppingCart,
    itineraryFeeTypeId,
    itinerary?.id,
    trackingInfo,
    createItinerarySelection,
    feeTypeId,
  ]);
};

export const useShoppingCartActions = () => {
  const { selectItineraryAndContinueToPassengers } =
    useContext(ItinerarySelectionActionsContext) || {};
  const { createShoppingBundle } = useCreateShoppingBundleActions() || {};
  const [feeType] = useFeeType();
  const searchId = useSearchId();
  const trackingInfo = useTrackingInfo();
  const feeTypeId = feeType?.id;
  const createItinerarySelection = useShoppingCartRequestCreator();
  return useMemo(
    () => ({
      selectItineraryAndContinue: (itinerary, request) => {
        const feeId = request?.feeTypeId || feeTypeId;
        const itinerarySelection = createItinerarySelection(
          itinerary,
          {
            ...request,
            feeTypeId: feeId,
            searchId,
          },
          trackingInfo
        );
        createShoppingBundle({
          ...itinerarySelection,
          feeTypeId: feeId,
        });
        selectItineraryAndContinueToPassengers(itinerarySelection);
      },
    }),
    [
      selectItineraryAndContinueToPassengers,
      trackingInfo,
      searchId,
      createItinerarySelection,
      feeTypeId,
      createShoppingBundle,
    ]
  );
};
